// Live invalidation over SSE. Notices say what changed, never content; the
// subscriber refetches through the bridge. The handler also runs (with a null
// notice) when the stream opens, the first time included, because anything
// that changed between the subscriber's initial fetch and the subscription
// going live was never announced; on reconnect; on a server `resync`; and on
// window focus, throttled (see `shouldRefetch`) because GraphQL refetches
// cannot be revalidated with ETags and count against the rate limit.
//
// A `revoked` notice says the stream no longer carries that topic's
// repository, and why (`RevokeReason`): the topic is dropped and never
// asked for again, the subscriber is told (`onRevoked`), and once no topic
// is left the stream is not reopened. A notice that names no topic (the
// bare form) cannot be routed, so it counts as a resync.
import { build } from './version.svelte';
import { MOCK } from '../mock/flag';
import { onIdentityChange } from './identity';
import { github, rateLimitedUntil } from './gh';

/** Why the handler was called without a notice. */
export type Trigger = 'open' | 'reconnect' | 'resync' | 'focus';

/** Minimum age of the last successful load before focus refetches it. */
export const FOCUS_REFETCH_AFTER = 60_000;

/**
 * Whether a subscriber should refetch for `trigger` given when it last loaded
 * successfully. Focus refetches only stale data; nothing refetches while
 * GitHub's rate limit is in force.
 */
export function shouldRefetch(trigger: Trigger | 'notice', lastLoadAt: number, now = Date.now()): boolean {
  if (rateLimitedUntil()) return false;
  if (trigger === 'focus') return now - lastLoadAt >= FOCUS_REFETCH_AFTER;
  return true;
}

/** True while GitHub calls are paused for the rate limit. */
export function pausedForRateLimit(): boolean {
  return rateLimitedUntil() !== null;
}

/** Random delay added after a rate-limit pause, so tabs do not refetch at once. */
const RESUME_JITTER = 2000;

/**
 * Run `fn` once after GitHub's rate-limit pause ends (plus jitter); a
 * subscriber that dropped notices during the pause refetches then. If the
 * pause was extended meanwhile, waits for the new end. Returns a cancel
 * function.
 */
export function afterRateLimit(fn: () => void, jitter = RESUME_JITTER): () => void {
  return github.afterPause(fn, jitter);
}

export interface Notice {
  kind: string;
  action?: string;
  number?: number;
  head_sha?: string;
  [k: string]: unknown;
}

/** `topic` is the notice's topic, or the Trigger when `notice` is null. */
export type NoticeHandler = (topic: string, notice: Notice | null) => void;

/**
 * Why a topic was revoked: the user can no longer read its repository
 * (`no_access`), or no installation of the changelist App covers it any
 * more (`not_installed`: GitHub still shows it, changelist keeps nothing
 * for it).
 */
export type RevokeReason = 'no_access' | 'not_installed';

/** Called once per revoked topic, after it was dropped. */
export type RevokeHandler = (topic: string, reason: RevokeReason) => void;

const MIN_DELAY = 1000;
const MAX_DELAY = 30000;

export class LiveEvents {
  private unwatch = () => {};
  private source: EventSource | null = null;
  private delay = MIN_DELAY;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;
  private connectedOnce = false;
  private onFocus = () => this.handler('focus', null);
  /** Mock mode has no stream: `changelist:mock-notice` events stand in for it. */
  private onMock = (e: Event) => {
    const { topic, notice } = (e as CustomEvent<{ topic: string; notice: Notice | null }>).detail;
    if (notice === null) this.handler(topic, notice);
    else if (this.topics.includes(topic)) this.receive(topic, notice);
  };

  constructor(
    private topics: string[],
    private handler: NoticeHandler,
    private onRevoked: RevokeHandler = () => {},
  ) {
    this.unwatch = onIdentityChange(() => this.close());
    window.addEventListener('focus', this.onFocus);
    if (MOCK) window.addEventListener('changelist:mock-notice', this.onMock);
    if (!MOCK && typeof EventSource !== 'undefined' && topics.length) this.connect();
  }

  private connect() {
    if (this.closed) return;
    const url = '/api/events?topics=' + this.topics.map(encodeURIComponent).join(',');
    const es = new EventSource(url, { withCredentials: true });
    this.source = es;
    es.onopen = () => {
      this.delay = MIN_DELAY;
      // A reconnect may follow a deploy: is this tab's build still the one served?
      if (this.connectedOnce) void build.check();
      this.handler(this.connectedOnce ? 'reconnect' : 'open', null);
      this.connectedOnce = true;
    };
    es.onmessage = (ev) => this.dispatch(ev);
    for (const name of ['notice', 'invalidate']) es.addEventListener(name, (ev) => this.dispatch(ev as MessageEvent));
    es.onerror = () => {
      es.close();
      this.source = null;
      if (this.closed || !this.topics.length) return;
      this.timer = setTimeout(() => this.connect(), this.delay + Math.random() * 500);
      this.delay = Math.min(MAX_DELAY, this.delay * 2);
    };
  }

  private dispatch(ev: MessageEvent) {
    let data: { topic?: string; notice?: Notice } & Notice;
    try {
      data = JSON.parse(ev.data);
    } catch {
      return; // keepalives and malformed payloads are ignored
    }
    this.receive(data.topic, data.notice ?? data);
  }

  private receive(topic: string | undefined, notice: Notice) {
    if (notice.kind === 'revoked') {
      if (topic === undefined) return;
      this.topics = this.topics.filter((t) => t !== topic);
      this.onRevoked(topic, notice.reason === 'not_installed' ? 'not_installed' : 'no_access');
      return;
    }
    // The server could not tell what changed (outbox gap), or the notice
    // cannot be routed: refetch everything.
    if (notice.kind === 'resync' || topic === undefined) this.handler('resync', null);
    else this.handler(topic, notice);
  }

  close() {
    this.closed = true;
    this.unwatch();
    window.removeEventListener('focus', this.onFocus);
    window.removeEventListener('changelist:mock-notice', this.onMock);
    if (this.timer) clearTimeout(this.timer);
    this.source?.close();
  }
}
